import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import * as Tabs from '@radix-ui/react-tabs';
import { toast } from 'sonner';
import { supabase } from '@/lib/supabase';
import { Loader2, PlusCircle, Trash2 } from 'lucide-react';
import type { PlanStatus } from '@shared/index';

interface PlanEditorProps {
  mode?: 'create' | 'edit';
}

interface FeatureState {
  id?: string;
  bullet: string;
  tierTag?: string;
  sort?: number;
}

interface PricingState {
  id?: string;
  billingCycle: 'monthly' | 'annual';
  baseCents: number;
  perExtraSystemFactor?: number | null;
  surcharges: string;
}

interface AddonState {
  id?: string;
  addonKey: string;
  name: string;
  description?: string;
  priceCents: number;
  isActive: boolean;
  sort?: number;
}

interface TermsState {
  id?: string;
  title: string;
  bodyMarkdown: string;
  excerpt?: string;
  isCurrent?: boolean;
  version?: number;
}

interface RulesState {
  id?: string;
  zipAllowlist: string;
  zipBlocklist: string;
  countyAllow: string;
  channelAllow: string;
  weekdaysAllowed: string;
  effectiveStart?: string | null;
  effectiveEnd?: string | null;
}

interface FormState {
  id?: string;
  groupId: string | null;
  name: string;
  slug: string;
  subtitle: string;
  status: PlanStatus;
  heroFlags: string;
  features: FeatureState[];
  pricing: PricingState[];
  addons: AddonState[];
  terms: TermsState;
  rules: RulesState;
}

interface InitialTermsSnapshot {
  current?: TermsState;
  draft?: TermsState;
}

const emptyFormState: FormState = {
  groupId: null,
  name: '',
  slug: '',
  subtitle: '',
  status: 'draft',
  heroFlags: '{\n  "badge": ""\n}',
  features: [],
  pricing: [
    { billingCycle: 'monthly', baseCents: 0, perExtraSystemFactor: null, surcharges: '{}' },
    { billingCycle: 'annual', baseCents: 0, perExtraSystemFactor: null, surcharges: '{}' },
  ],
  addons: [],
  terms: {
    title: 'Terms & Conditions',
    bodyMarkdown: '# Terms\n\nDescribe the plan coverage.',
    excerpt: '',
  },
  rules: {
    zipAllowlist: '',
    zipBlocklist: '',
    countyAllow: '',
    channelAllow: '',
    weekdaysAllowed: '1,2,3,4,5,6,7',
    effectiveStart: null,
    effectiveEnd: null,
  },
};

const PlanEditorPage = ({ mode }: PlanEditorProps) => {
  const params = useParams();
  const navigate = useNavigate();
  const isCreate = mode === 'create' || params.planId === 'new';
  const [formState, setFormState] = useState<FormState>(emptyFormState);
  const [planGroups, setPlanGroups] = useState<Array<{ id: string; name: string; brand: string; category: string }>>([]);
  const [isLoading, setIsLoading] = useState(!isCreate);
  const [isSaving, setIsSaving] = useState(false);
  const [hasEdits, setHasEdits] = useState(false);
  const [initialTerms, setInitialTerms] = useState<InitialTermsSnapshot>({});

  const planId = isCreate ? undefined : params.planId;

  useEffect(() => {
    const fetchGroups = async () => {
      if (!supabase) return;
      const { data, error } = await supabase
        .from('plan_groups')
        .select('id, name, brand, category')
        .order('brand');
      if (!error && data) {
        setPlanGroups(data);
      }
    };

    fetchGroups();
  }, []);

  useEffect(() => {
    const loadPlan = async () => {
      if (!supabase || !planId) {
        setIsLoading(false);
        return;
      }

      const select = `
        id,
        group_id,
        slug,
        name,
        subtitle,
        status,
        hero_flags,
        created_at,
        updated_at,
        rev,
        published_rev,
        published_at,
        published_terms_version,
        plan_groups:plan_groups ( id, name, brand, category ),
        plan_features ( id, bullet, tier_tag, sort ),
        plan_pricing ( id, billing_cycle, base_cents, per_extra_system_factor, surcharges ),
        plan_addons ( id, addon_key, name, description, price_cents, is_active, sort ),
        plan_terms ( id, version, title, body_markdown, excerpt, effective_at, superseded_at, is_current ),
        plan_rules ( id, zip_allowlist, zip_blocklist, county_allow, weekdays_allowed, channel_allow, effective_start, effective_end )
      `;

      const { data, error } = await supabase
        .from('plans')
        .select(select)
        .eq('id', planId)
        .maybeSingle();

      if (error) {
        toast.error('Failed to load plan', { description: error.message });
        setIsLoading(false);
        return;
      }

      if (!data) {
        toast.error('Plan not found');
        navigate('/plans');
        return;
      }

      const heroFlagsString = JSON.stringify(data.hero_flags ?? {}, null, 2);

      const features: FeatureState[] = (data.plan_features ?? [])
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
        .map((feature) => ({
          id: feature.id,
          bullet: feature.bullet,
          tierTag: feature.tier_tag ?? undefined,
          sort: feature.sort ?? undefined,
        }));

      const pricingOrder: Record<PricingState['billingCycle'], number> = {
        monthly: 0,
        annual: 1,
      };

      const pricing: PricingState[] = (data.plan_pricing ?? [])
        .map((pricingRow): PricingState => ({
          id: pricingRow.id,
          billingCycle: pricingRow.billing_cycle === 'annual' ? 'annual' : 'monthly',
          baseCents: pricingRow.base_cents,
          perExtraSystemFactor: pricingRow.per_extra_system_factor,
          surcharges: JSON.stringify(pricingRow.surcharges ?? {}, null, 2),
        }))
        .sort((a, b) => pricingOrder[a.billingCycle] - pricingOrder[b.billingCycle]);

      const addons: AddonState[] = (data.plan_addons ?? [])
        .sort((a, b) => (a.sort ?? 0) - (b.sort ?? 0))
        .map((addon) => ({
          id: addon.id,
          addonKey: addon.addon_key,
          name: addon.name,
          description: addon.description ?? undefined,
          priceCents: addon.price_cents,
          isActive: addon.is_active,
          sort: addon.sort ?? undefined,
        }));

      const currentTerms = (data.plan_terms ?? []).find((term) => term.is_current === true);
      const draftTerms = (data.plan_terms ?? []).find((term) => term.is_current === false);

      setInitialTerms({
        current: currentTerms
          ? {
              id: currentTerms.id,
              title: currentTerms.title,
              bodyMarkdown: currentTerms.body_markdown,
              excerpt: currentTerms.excerpt ?? undefined,
              isCurrent: true,
              version: currentTerms.version,
            }
          : undefined,
        draft: draftTerms
          ? {
              id: draftTerms.id,
              title: draftTerms.title,
              bodyMarkdown: draftTerms.body_markdown,
              excerpt: draftTerms.excerpt ?? undefined,
              isCurrent: false,
              version: draftTerms.version,
            }
          : undefined,
      });

      const rulesRow = data.plan_rules?.[0];

      setFormState({
        id: data.id,
        groupId: data.group_id,
        name: data.name,
        slug: data.slug,
        subtitle: data.subtitle ?? '',
        status: data.status as PlanStatus,
        heroFlags: heroFlagsString,
        features,
        pricing: pricing.length ? pricing : emptyFormState.pricing,
        addons,
        terms: {
          id: draftTerms?.id ?? currentTerms?.id,
          title: (draftTerms ?? currentTerms)?.title ?? 'Terms & Conditions',
          bodyMarkdown: (draftTerms ?? currentTerms)?.body_markdown ?? '',
          excerpt: (draftTerms ?? currentTerms)?.excerpt ?? undefined,
        },
        rules: {
          id: rulesRow?.id ?? undefined,
          zipAllowlist: (rulesRow?.zip_allowlist ?? []).join(','),
          zipBlocklist: (rulesRow?.zip_blocklist ?? []).join(','),
          countyAllow: (rulesRow?.county_allow ?? []).join(','),
          channelAllow: (rulesRow?.channel_allow ?? []).join(','),
          weekdaysAllowed: (rulesRow?.weekdays_allowed ?? [1, 2, 3, 4, 5, 6, 7]).join(','),
          effectiveStart: rulesRow?.effective_start ?? null,
          effectiveEnd: rulesRow?.effective_end ?? null,
        },
      });

      setIsLoading(false);
    };

    loadPlan();
  }, [planId, navigate]);

  const handleFieldChange = <K extends keyof FormState>(key: K, value: FormState[K]) => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      [key]: value,
    }));
  };

  const addFeature = () => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      features: [...prev.features, { bullet: '', tierTag: undefined }],
    }));
  };

  const removeFeature = (index: number) => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      features: prev.features.filter((_, idx) => idx !== index),
    }));
  };

  const upsertFeature = (index: number, patch: Partial<FeatureState>) => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      features: prev.features.map((feature, idx) => (idx === index ? { ...feature, ...patch } : feature)),
    }));
  };

  const addAddon = () => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      addons: [...prev.addons, { addonKey: '', name: '', priceCents: 0, isActive: true }],
    }));
  };

  const removeAddon = (index: number) => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      addons: prev.addons.filter((_, idx) => idx !== index),
    }));
  };

  const upsertAddon = (index: number, patch: Partial<AddonState>) => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      addons: prev.addons.map((addon, idx) => (idx === index ? { ...addon, ...patch } : addon)),
    }));
  };

  const upsertPricing = (index: number, patch: Partial<PricingState>) => {
    setHasEdits(true);
    setFormState((prev) => ({
      ...prev,
      pricing: prev.pricing.map((pricing, idx) => (idx === index ? { ...pricing, ...patch } : pricing)),
    }));
  };


  const savePlan = useCallback(async () => {
    if (!supabase) return;
    setIsSaving(true);
    try {
      const heroFlagsPayload = (() => {
        try {
          return JSON.parse(formState.heroFlags || '{}');
        } catch (error) {
          throw new Error('Hero flags JSON is invalid');
        }
      })();

      let nextPlanId = formState.id;

      if (!nextPlanId) {
        const { data, error } = await supabase
          .from('plans')
          .insert({
            group_id: formState.groupId,
            slug: formState.slug,
            name: formState.name,
            subtitle: formState.subtitle,
            status: formState.status,
            hero_flags: heroFlagsPayload,
          })
          .select('id')
          .single();

        if (error || !data) {
          throw error ?? new Error('Failed to create plan');
        }

        nextPlanId = data.id;
        setFormState((prev) => ({ ...prev, id: nextPlanId }));
      } else {
        const { error } = await supabase
          .from('plans')
          .update({
            group_id: formState.groupId,
            slug: formState.slug,
            name: formState.name,
            subtitle: formState.subtitle,
            status: formState.status,
            hero_flags: heroFlagsPayload,
          })
          .eq('id', nextPlanId);

        if (error) throw error;
      }

      const planIdToUse = nextPlanId!;

      await supabase.from('plan_features').delete().eq('plan_id', planIdToUse);
      if (formState.features.length) {
        const insertPayload = formState.features.map((feature, index) => ({
          plan_id: planIdToUse,
          bullet: feature.bullet,
          tier_tag: feature.tierTag ?? null,
          sort: index,
        }));
        const { error } = await supabase.from('plan_features').insert(insertPayload);
        if (error) throw error;
      }

      await supabase.from('plan_pricing').delete().eq('plan_id', planIdToUse);
      if (formState.pricing.length) {
        const pricingPayload = formState.pricing.map((pricing) => {
          let surcharges: Record<string, unknown> = {};
          try {
            surcharges = JSON.parse(pricing.surcharges || '{}');
          } catch (_err) {
            throw new Error(`Invalid surcharges JSON for ${pricing.billingCycle} pricing`);
          }
          return {
            plan_id: planIdToUse,
            billing_cycle: pricing.billingCycle,
            base_cents: pricing.baseCents,
            per_extra_system_factor: pricing.perExtraSystemFactor ?? null,
            surcharges,
          };
        });
        const { error } = await supabase.from('plan_pricing').insert(pricingPayload);
        if (error) throw error;
      }

      await supabase.from('plan_addons').delete().eq('plan_id', planIdToUse);
      if (formState.addons.length) {
        const addonPayload = formState.addons.map((addon, index) => ({
          plan_id: planIdToUse,
          addon_key: addon.addonKey,
          name: addon.name,
          description: addon.description ?? null,
          price_cents: addon.priceCents,
          is_active: addon.isActive,
          sort: index,
        }));
        const { error } = await supabase.from('plan_addons').insert(addonPayload);
        if (error) throw error;
      }

      const rulesPayload = {
        plan_id: planIdToUse,
        zip_allowlist: formState.rules.zipAllowlist ? formState.rules.zipAllowlist.split(',').map((zip) => zip.trim()).filter(Boolean) : [],
        zip_blocklist: formState.rules.zipBlocklist ? formState.rules.zipBlocklist.split(',').map((zip) => zip.trim()).filter(Boolean) : [],
        county_allow: formState.rules.countyAllow ? formState.rules.countyAllow.split(',').map((x) => x.trim()).filter(Boolean) : [],
        channel_allow: formState.rules.channelAllow ? formState.rules.channelAllow.split(',').map((x) => x.trim()).filter(Boolean) : [],
        weekdays_allowed: formState.rules.weekdaysAllowed ? formState.rules.weekdaysAllowed.split(',').map((x) => Number(x.trim())).filter(Boolean) : [1, 2, 3, 4, 5, 6, 7],
        effective_start: formState.rules.effectiveStart ?? null,
        effective_end: formState.rules.effectiveEnd ?? null,
      };

      await supabase.from('plan_rules').delete().eq('plan_id', planIdToUse);
      if (
        rulesPayload.zip_allowlist.length ||
        rulesPayload.zip_blocklist.length ||
        rulesPayload.county_allow.length ||
        rulesPayload.channel_allow.length
      ) {
        const { error } = await supabase.from('plan_rules').insert(rulesPayload);
        if (error) throw error;
      }

      if (formState.status === 'published') {
        const draftExists = initialTerms.draft ?? null;
        if (draftExists) {
          const { error } = await supabase
            .from('plan_terms')
            .update({
              title: formState.terms.title,
              body_markdown: formState.terms.bodyMarkdown,
              excerpt: formState.terms.excerpt ?? null,
              is_current: false,
            })
            .eq('id', draftExists.id);
          if (error) throw error;
        } else if (
          initialTerms.current &&
          (initialTerms.current.title !== formState.terms.title ||
            initialTerms.current.bodyMarkdown !== formState.terms.bodyMarkdown ||
            initialTerms.current.excerpt !== formState.terms.excerpt)
        ) {
          const nextVersion = (initialTerms.current?.version ?? 1) + 1;
          const { error } = await supabase
            .from('plan_terms')
            .insert({
              plan_id: planIdToUse,
              version: nextVersion,
              title: formState.terms.title,
              body_markdown: formState.terms.bodyMarkdown,
              excerpt: formState.terms.excerpt ?? null,
              is_current: false,
              effective_at: new Date().toISOString(),
            });
          if (error) throw error;
        }
      } else {
        if (initialTerms.current) {
          const { error } = await supabase
            .from('plan_terms')
            .update({
              title: formState.terms.title,
              body_markdown: formState.terms.bodyMarkdown,
              excerpt: formState.terms.excerpt ?? null,
              is_current: true,
            })
            .eq('id', initialTerms.current.id);
          if (error) throw error;
        } else {
          const { error } = await supabase
            .from('plan_terms')
            .insert({
              plan_id: planIdToUse,
              version: 1,
              title: formState.terms.title,
              body_markdown: formState.terms.bodyMarkdown,
              excerpt: formState.terms.excerpt ?? null,
              is_current: true,
              effective_at: new Date().toISOString(),
            });
          if (error) throw error;
        }
      }

      const { data: latestTerms, error: latestTermsError } = await supabase
        .from('plan_terms')
        .select('id, version, title, body_markdown, excerpt, is_current')
        .eq('plan_id', planIdToUse);

      if (!latestTermsError && latestTerms) {
        const current = latestTerms.find((term) => term.is_current);
        const draft = latestTerms.find((term) => !term.is_current);
        setInitialTerms({
          current: current
            ? { id: current.id, title: current.title, bodyMarkdown: current.body_markdown, excerpt: current.excerpt ?? undefined, isCurrent: true, version: current.version }
            : undefined,
          draft: draft
            ? { id: draft.id, title: draft.title, bodyMarkdown: draft.body_markdown, excerpt: draft.excerpt ?? undefined, isCurrent: false, version: draft.version }
            : undefined,
        });
        if (draft) {
          setFormState((prev) => ({ ...prev, terms: { ...prev.terms, id: draft.id } }));
        } else if (current) {
          setFormState((prev) => ({ ...prev, terms: { ...prev.terms, id: current.id } }));
        }
      }

      toast.success('Plan saved');
      setHasEdits(false);
      if (isCreate) {
        navigate(`/plans/${planIdToUse}`);
      }
    } catch (error) {
      toast.error('Failed to save plan', {
        description: error instanceof Error ? error.message : String(error),
      });
    } finally {
      setIsSaving(false);
    }
  }, [formState, initialTerms, isCreate, navigate]);

  const publishPlan = async () => {
    if (!supabase || !formState.id) return;
    try {
      const { data, error } = await supabase.functions.invoke('plan-publish', {
        body: { plan_id: formState.id, notes: 'Published via admin editor' },
      });
      if (error) throw error;
      toast.success('Plan published', { description: `Revision r${data?.publishedRevision}` });
      setFormState((prev) => ({ ...prev, status: 'published' }));
      setHasEdits(false);
    } catch (error) {
      toast.error('Publish failed', { description: error instanceof Error ? error.message : String(error) });
    }
  };

  const suspendPlan = async () => {
    if (!supabase || !formState.id) return;
    try {
      const { error } = await supabase.functions.invoke('plan-suspend', {
        body: { plan_id: formState.id, reason: 'Suspended from editor' },
      });
      if (error) throw error;
      toast.success('Plan suspended');
      setFormState((prev) => ({ ...prev, status: 'suspended' }));
    } catch (error) {
      toast.error('Suspend failed', { description: error instanceof Error ? error.message : String(error) });
    }
  };

  useEffect(() => {
    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (hasEdits) {
        event.preventDefault();
        event.returnValue = '';
      }
    };
    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [hasEdits]);

  useEffect(() => {
    const handleShortcut = (event: KeyboardEvent) => {
      if ((event.metaKey || event.ctrlKey) && event.key.toLowerCase() === 's') {
        event.preventDefault();
        if (!isSaving) {
          void savePlan();
        }
      }
    };
    window.addEventListener('keydown', handleShortcut);
    return () => window.removeEventListener('keydown', handleShortcut);
  }, [isSaving, savePlan, hasEdits]);

  if (isLoading) {
    return (
      <div className="flex h-96 items-center justify-center text-slate-400">
        <Loader2 className="mr-2 h-5 w-5 animate-spin" /> Loading plan…
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <header className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-100">{isCreate ? 'Create Plan' : formState.name || 'Edit Plan'}</h1>
          <p className="text-sm text-slate-400">
            Configure plan details, features, pricing, rules, and terms. Unsaved changes are local until you save.
          </p>
        </div>
        <div className="flex items-center gap-2">
          {!isCreate && (
            <button
              type="button"
              onClick={suspendPlan}
              className="rounded-md border border-amber-500 px-3 py-2 text-sm font-medium text-amber-300 hover:bg-amber-500/10"
            >
              Suspend
            </button>
          )}
          <button
            type="button"
            onClick={savePlan}
            disabled={isSaving}
            className="rounded-md border border-slate-700 px-4 py-2 text-sm font-medium text-slate-100 hover:bg-slate-800/60 disabled:opacity-50"
          >
            {isSaving ? 'Saving…' : 'Save draft'}
          </button>
          <button
            type="button"
            onClick={publishPlan}
            disabled={!formState.id}
            className="rounded-md bg-emerald-500 px-4 py-2 text-sm font-semibold text-white shadow hover:bg-emerald-600 disabled:opacity-50"
          >
            Publish
          </button>
        </div>
      </header>

      <Tabs.Root defaultValue="details" orientation="horizontal">
        <Tabs.List className="flex gap-2 border-b border-slate-800 pb-2">
          <TabTrigger value="details">Details</TabTrigger>
          <TabTrigger value="features">Features</TabTrigger>
          <TabTrigger value="pricing">Pricing</TabTrigger>
          <TabTrigger value="addons">Add-ons</TabTrigger>
          <TabTrigger value="terms">Terms</TabTrigger>
          <TabTrigger value="rules">Rules</TabTrigger>
        </Tabs.List>

        <Tabs.Content value="details" className="pt-4">
          <section className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Plan Name</label>
              <input
                type="text"
                value={formState.name}
                onChange={(event) => handleFieldChange('name', event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Slug</label>
              <input
                type="text"
                value={formState.slug}
                onChange={(event) => handleFieldChange('slug', event.target.value)}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Status</label>
              <select
                value={formState.status}
                onChange={(event) => handleFieldChange('status', event.target.value as PlanStatus)}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              >
                <option value="draft">Draft</option>
                <option value="review">Review</option>
                <option value="published">Published</option>
                <option value="suspended">Suspended</option>
                <option value="archived">Archived</option>
              </select>
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Plan Group</label>
              <select
                value={formState.groupId ?? ''}
                onChange={(event) => handleFieldChange('groupId', event.target.value || null)}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              >
                <option value="">Unassigned</option>
                {planGroups.map((group) => (
                  <option key={group.id} value={group.id}>
                    {group.brand} · {group.name} ({group.category})
                  </option>
                ))}
              </select>
            </div>
          </section>

          <section className="mt-4 grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Subtitle</label>
              <textarea
                value={formState.subtitle}
                onChange={(event) => handleFieldChange('subtitle', event.target.value)}
                rows={3}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>

            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Hero Flags (JSON)</label>
              <textarea
                value={formState.heroFlags}
                onChange={(event) => handleFieldChange('heroFlags', event.target.value)}
                rows={8}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-mono text-emerald-200"
              />
            </div>
          </section>
        </Tabs.Content>

        <Tabs.Content value="features" className="pt-4">
          <div className="space-y-3">
            {formState.features.map((feature, index) => (
              <div key={index} className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-2">
                    <label className="text-xs uppercase tracking-wide text-slate-500">Feature bullet</label>
                    <input
                      type="text"
                      value={feature.bullet}
                      onChange={(event) => upsertFeature(index, { bullet: event.target.value })}
                      className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    />
                    <label className="text-xs uppercase tracking-wide text-slate-500">Tier tag</label>
                    <input
                      type="text"
                      value={feature.tierTag ?? ''}
                      onChange={(event) => upsertFeature(index, { tierTag: event.target.value })}
                      className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    />
                  </div>
                  <button
                    type="button"
                    onClick={() => removeFeature(index)}
                    className="rounded-md border border-slate-700 p-2 text-slate-300 hover:bg-slate-800/60"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addFeature}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-slate-700 px-4 py-3 text-sm text-slate-300 hover:bg-slate-800/60"
            >
              <PlusCircle size={16} /> Add feature
            </button>
          </div>
        </Tabs.Content>

        <Tabs.Content value="pricing" className="pt-4">
          <div className="grid gap-4 md:grid-cols-2">
            {formState.pricing.map((pricing, index) => (
              <div key={index} className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
                <h3 className="mb-3 text-sm font-semibold text-slate-100 capitalize">{pricing.billingCycle} pricing</h3>
                <label className="text-xs uppercase tracking-wide text-slate-500">Base price (cents)</label>
                <input
                  type="number"
                  value={pricing.baseCents}
                  onChange={(event) => upsertPricing(index, { baseCents: Number(event.target.value) })}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                />
                <label className="mt-3 text-xs uppercase tracking-wide text-slate-500">Per extra system factor</label>
                <input
                  type="number"
                  step="0.1"
                  value={pricing.perExtraSystemFactor ?? 0}
                  onChange={(event) => {
                  const value = event.target.value;
                  upsertPricing(index, { perExtraSystemFactor: value === '' ? null : Number(value) });
                }}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                />
                <label className="mt-3 text-xs uppercase tracking-wide text-slate-500">Surcharges (JSON)</label>
                <textarea
                  value={pricing.surcharges}
                  onChange={(event) => upsertPricing(index, { surcharges: event.target.value })}
                  rows={4}
                  className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-mono text-emerald-200"
                />
              </div>
            ))}
          </div>
        </Tabs.Content>

        <Tabs.Content value="addons" className="pt-4">
          <div className="space-y-3">
            {formState.addons.map((addon, index) => (
              <div key={index} className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
                <div className="flex items-start gap-3">
                  <div className="flex-1 space-y-2">
                    <label className="text-xs uppercase tracking-wide text-slate-500">Addon key</label>
                    <input
                      type="text"
                      value={addon.addonKey}
                      onChange={(event) => upsertAddon(index, { addonKey: event.target.value })}
                      className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    />
                    <label className="text-xs uppercase tracking-wide text-slate-500">Name</label>
                    <input
                      type="text"
                      value={addon.name}
                      onChange={(event) => upsertAddon(index, { name: event.target.value })}
                      className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    />
                    <label className="text-xs uppercase tracking-wide text-slate-500">Description</label>
                    <textarea
                      value={addon.description ?? ''}
                      onChange={(event) => upsertAddon(index, { description: event.target.value })}
                      rows={3}
                      className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    />
                    <label className="text-xs uppercase tracking-wide text-slate-500">Price (cents)</label>
                    <input
                      type="number"
                      value={addon.priceCents}
                      onChange={(event) => upsertAddon(index, { priceCents: Number(event.target.value) })}
                      className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
                    />
                    <label className="flex items-center gap-2 text-xs uppercase tracking-wide text-slate-500">
                      <input
                        type="checkbox"
                        checked={addon.isActive}
                        onChange={(event) => upsertAddon(index, { isActive: event.target.checked })}
                        className="rounded border-slate-700 bg-slate-900"
                      />
                      Active
                    </label>
                  </div>
                  <button
                    type="button"
                    onClick={() => removeAddon(index)}
                    className="rounded-md border border-slate-700 p-2 text-slate-300 hover:bg-slate-800/60"
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              </div>
            ))}
            <button
              type="button"
              onClick={addAddon}
              className="flex w-full items-center justify-center gap-2 rounded-md border border-dashed border-slate-700 px-4 py-3 text-sm text-slate-300 hover:bg-slate-800/60"
            >
              <PlusCircle size={16} /> Add add-on
            </button>
          </div>
        </Tabs.Content>

        <Tabs.Content value="terms" className="pt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Title</label>
              <input
                type="text"
                value={formState.terms.title}
                onChange={(event) => handleFieldChange('terms', { ...formState.terms, title: event.target.value })}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
              <label className="mt-4 text-xs uppercase tracking-wide text-slate-500">Excerpt</label>
              <textarea
                value={formState.terms.excerpt ?? ''}
                onChange={(event) => handleFieldChange('terms', { ...formState.terms, excerpt: event.target.value })}
                rows={3}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
              <label className="text-xs uppercase tracking-wide text-slate-500">Markdown body</label>
              <textarea
                value={formState.terms.bodyMarkdown}
                onChange={(event) => handleFieldChange('terms', { ...formState.terms, bodyMarkdown: event.target.value })}
                rows={16}
                className="mt-1 w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-xs font-mono text-slate-100"
              />
            </div>
          </div>
          {formState.status === 'published' && (
            <div className="mt-4 rounded-md border border-sky-500/40 bg-sky-500/10 p-3 text-sm text-sky-200">
              Changes will publish as a new terms version. Existing subscribers retain their original version.
            </div>
          )}
        </Tabs.Content>

        <Tabs.Content value="rules" className="pt-4">
          <div className="grid gap-4 md:grid-cols-2">
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 space-y-3">
              <label className="text-xs uppercase tracking-wide text-slate-500">ZIP allowlist (comma separated)</label>
              <input
                type="text"
                value={formState.rules.zipAllowlist}
                onChange={(event) => handleFieldChange('rules', { ...formState.rules, zipAllowlist: event.target.value })}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
              <label className="text-xs uppercase tracking-wide text-slate-500">ZIP blocklist</label>
              <input
                type="text"
                value={formState.rules.zipBlocklist}
                onChange={(event) => handleFieldChange('rules', { ...formState.rules, zipBlocklist: event.target.value })}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
              <label className="text-xs uppercase tracking-wide text-slate-500">Counties</label>
              <input
                type="text"
                value={formState.rules.countyAllow}
                onChange={(event) => handleFieldChange('rules', { ...formState.rules, countyAllow: event.target.value })}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
              <label className="text-xs uppercase tracking-wide text-slate-500">Channels</label>
              <input
                type="text"
                value={formState.rules.channelAllow}
                onChange={(event) => handleFieldChange('rules', { ...formState.rules, channelAllow: event.target.value })}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
              <label className="text-xs uppercase tracking-wide text-slate-500">Weekdays (1-7)</label>
              <input
                type="text"
                value={formState.rules.weekdaysAllowed}
                onChange={(event) => handleFieldChange('rules', { ...formState.rules, weekdaysAllowed: event.target.value })}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
            </div>
            <div className="rounded-lg border border-slate-800 bg-slate-900/60 p-4 space-y-3">
              <label className="text-xs uppercase tracking-wide text-slate-500">Effective start</label>
              <input
                type="datetime-local"
                value={formState.rules.effectiveStart ?? ''}
                onChange={(event) => handleFieldChange('rules', { ...formState.rules, effectiveStart: event.target.value || null })}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
              <label className="text-xs uppercase tracking-wide text-slate-500">Effective end</label>
              <input
                type="datetime-local"
                value={formState.rules.effectiveEnd ?? ''}
                onChange={(event) => handleFieldChange('rules', { ...formState.rules, effectiveEnd: event.target.value || null })}
                className="w-full rounded-md border border-slate-700 bg-slate-900 px-3 py-2 text-sm text-slate-100"
              />
              <div className="rounded-md border border-slate-700 bg-slate-900/80 p-3 text-xs text-slate-400">
                <p className="font-semibold text-slate-200">Current preview</p>
                <p>Allow ZIPs: {formState.rules.zipAllowlist || 'All'}</p>
                <p>Block ZIPs: {formState.rules.zipBlocklist || 'None'}</p>
                <p>Channels: {formState.rules.channelAllow || 'All'}</p>
                <p>Weekdays: {formState.rules.weekdaysAllowed}</p>
              </div>
            </div>
          </div>
        </Tabs.Content>
      </Tabs.Root>
    </div>
  );
};

interface TabTriggerProps {
  value: string;
  children: React.ReactNode;
}

const TabTrigger = ({ value, children }: TabTriggerProps) => (
  <Tabs.Trigger
    value={value}
    className="rounded-md px-3 py-2 text-sm font-medium text-slate-300 hover:bg-slate-800/60 data-[state=active]:bg-slate-800 data-[state=active]:text-white"
  >
    {children}
  </Tabs.Trigger>
);

export default PlanEditorPage;
